import {
  DEFAULT_NOTIFICATION_TOTALS,
  DEFAULT_NOTIFICATIONS_PER_STORE,
  INotification,
  INotificationPerStore,
  INotificationTotals,
} from '../models';

//#region FUNCTION removeDuplicateNotifications
/**
 * Removes duplicate notifications from the input array by comparing their `notificationId` property.
 *
 * @function
 * @param {INotification[]} notifications - The array of notifications to process.
 * @returns {INotification[]} A new array containing only unique notifications, preserving the first occurrence of each `notificationId`.
 *
 * @description
 * This function iterates over the provided notifications and filters out duplicates based on the `notificationId` field.
 * It maintains the first encountered notification for each unique `notificationId`.
 *
 * @example
 * const deduplicated = removeDuplicates([
 *   { notificationId: '1', ... },
 *   { notificationId: '2', ... },
 *   { notificationId: '1', ... }
 * ]);
 * // deduplicated contains only the first and second notifications
 */
export function removeDuplicates(
  notifications: INotification[],
): INotification[] {
  const seen = new Set<string>();
  return notifications.filter((notification: INotification) => {
    if (seen.has(notification.notificationId)) {
      return false;
    }
    seen.add(notification.notificationId);
    return true;
  });
}

//#endregion

//region FUNCTION groupNotificationsPerUserStore
/**
 * Groups notifications per user store and calculates the total number of notifications.
 *
 * This function organizes notifications based on `storeId`, grouping them under each store
 * while also computing the total number of notifications across all stores.
 *
 * @param {string[]} storeIds - An array of store IDs for which notifications need to be grouped.
 * @param {INotification[]} notifications - The array of notifications to be grouped by store ID.
 *
 * @returns {Object} An object containing:
 * - `totalNotifications` (number): The total count of notifications across all stores.
 * - `notificationsGrouped` (Object): An object where each key is a store ID, and
 *   the value is an `INotificationPerStore` containing the count and the list of notifications for that store.
 *
 * @example
 * const storeIds = ['store1', 'store2'];
 * const notifications = [
 *   { storeId: 'store1', event: 'order', response: 'success', ... },
 *   { storeId: 'store2', event: 'stock', response: 'low', ... },
 *   { storeId: 'store1', event: 'refund', response: 'processed', ... }
 * ];
 * const groupedNotifications = groupNotificationsPerUserStore(storeIds, notifications);
 * console.info(groupedNotifications.totalNotifications); // 3
 * console.info(groupedNotifications.notificationsGrouped);
 */
export function groupNotificationsPerUserStore(
  storeIds: string[],
  notifications: INotification[],
): {
  totals: INotificationTotals,
  notificationsGrouped: Record<string, INotificationPerStore>
} {
  const storeNotifications: Record<string, INotificationPerStore> = {};
  const totals: INotificationTotals = {...DEFAULT_NOTIFICATION_TOTALS};
  storeIds.forEach((storeId: string) => {
    // Assign zero values to totals
    storeNotifications[storeId] = {...DEFAULT_NOTIFICATIONS_PER_STORE};
    // Filter according to store
    storeNotifications[storeId].notifications = notifications.filter((notification: INotification) => notification.storeId === storeId);
    // Count notifications for store
    storeNotifications[storeId].total = storeNotifications[storeId].notifications.length;
    // Count read notifications
    storeNotifications[storeId].read = storeNotifications[storeId].notifications
      .filter((notification: INotification) => notification?.read?.length > 0).length;
    // Count failed status
    storeNotifications[storeId].failed = storeNotifications[storeId].notifications
      .filter((notification: INotification) => notification?.success === false).length;
    // Count unread notifications
    const unread = storeNotifications[storeId].notifications
      .filter((notification: INotification) => notification?.read?.length < 1);
    storeNotifications[storeId].unread = unread.length;
    // Count unsuccessful unread
    storeNotifications[storeId].unreadFailed = unread
      .filter((notification: INotification) => notification?.success === false).length;
    // Add to totals
    totals.total += storeNotifications[storeId].total;
    totals.read += storeNotifications[storeId].read;
    totals.unread += storeNotifications[storeId].unread;
    totals.failed += storeNotifications[storeId].failed;
    totals.unreadFailed += storeNotifications[storeId].unreadFailed;
  });
  return {
    totals: totals,
    notificationsGrouped: storeNotifications,
  };
}

/**
 * Replaces a notification in a flat list by matching notificationId.
 */
export function replaceNotificationInList(
  notifications: INotification[],
  updated: INotification,
): INotification[] {
  const index: number = notifications.findIndex(
    (notification: INotification) => notification.notificationId === updated.notificationId,
  );
  if (index === -1) {
    return notifications;
  }
  const next: INotification[] = [...notifications];
  next[index] = {...next[index], ...updated};
  return next;
}

/**
 * Replaces a notification inside grouped per-store notification collections.
 */
export function replaceNotificationInGrouped(
  grouped: Record<string, INotificationPerStore>,
  updated: INotification,
): Record<string, INotificationPerStore> {
  const perStore: INotificationPerStore | undefined = grouped[updated.storeId];
  if (!perStore) {
    return grouped;
  }
  return {
    ...grouped,
    [updated.storeId]: {
      ...perStore,
      notifications: replaceNotificationInList(perStore.notifications, updated),
    },
  };
}

//endregion
